import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { apiBaseOrigin, configuredApiOrigin } from '../../../shared/config/api-origin';
import { syntheticBusinessEnabled } from '../../../shared/config/platform-services';
import { BusinessWorkspaceSelector } from '../../../shared/platform/BusinessWorkspaceSelector';
import { PlatformHeader } from '../../../shared/platform/PlatformHeader';
import { TitleBackdrop } from '../../../shared/platform/TitleBackdrop';
import { FrontendCondition } from '../../../shared/semantic/index';
import { Icon } from '../../../shared/ui/Primitives';
import '../../overview/ui/overview.css';
import { createLiveScenarioKnowledgeAdapter } from '../../scenario-center/knowledge/adapters/live-scenario-knowledge-adapter';
import { ScenarioKnowledgeChain } from '../../scenario-center/ui/ScenarioKnowledgeChain';
import { AssetDataManagementPrototype } from '../assets/ui/AssetDataManagementPrototype';
import { LocalAssetPanel } from '../assets/ui/LocalAssetPanel';
import { SynLiveLocalAsset, synLiveAssetEnabled } from '../assets/ui/SynLiveLocalAsset';
import {
  createCatalogReadSession,
  initialCatalogReadState,
  type CatalogReadState,
} from '../consumer/read-catalog';
import {
  CENTER_DATA_MODE,
  createCenterReadSession,
  initialCenterReadState,
  type CenterReadState,
} from '../consumer/read-center';
import type { CapabilityDefinitionCatalogReadPort } from '../definition/adapters/capability-definition-catalog-port';
import type { CapabilityDefinitionReadPort } from '../definition/adapters/capability-definition-port';
import { createLiveCapabilityDefinitionAdapter } from '../definition/adapters/live-capability-definition-adapter';
import { createLiveCapabilityDefinitionCatalogAdapter } from '../definition/adapters/live-capability-definition-catalog-adapter';
import { type CapabilityDefinitionSnapshotV1 } from '../definition/consumer/contract';
import { definitionFilterOptions, filterDefinitions } from '../domain/collection';
import './capability-equipment.css';

const fields: readonly [keyof CapabilityDefinitionSnapshotV1, string][] = [
  ['capabilityId', '能力 ID'],
  ['displayName', '定义名称'],
  ['capabilityType', '能力类型'],
  ['definitionVersion', '定义版本'],
  ['definitionDigest', '定义摘要'],
  ['definitionStatus', '定义生命周期'],
  ['aggregateVersion', '聚合版本'],
  ['originLabel', '来源标签'],
];
export function DefinitionFields({
  snapshot,
}: {
  readonly snapshot: CapabilityDefinitionSnapshotV1;
}) {
  return (
    <dl className="ca-center__fields">
      {fields.map(([key, label]) => (
        <div key={key}>
          <dt>{label}</dt>
          <dd data-field={key}>{snapshot[key]}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CenterReadSummary({ state }: { readonly state: CenterReadState }) {
  const result = state.result;
  let title = '尚未查询';
  let text = '输入能力 ID 并明确提交，从现有本地合成服务读取定义。';
  if (state.failure === 'REQUEST_REJECTED') {
    title = '请求已拒绝';
    text = '请输入非空能力 ID，且不可为 . 或 ..；本次没有取得定义。';
  } else if (state.failure === 'WIRE_INVALID') {
    title = 'Wire Invalid';
    text = '响应未通过既有合同校验；未取得可信定义，可重试。';
  } else if (state.failure === 'TRANSPORT_ERROR') {
    title = '传输错误';
    text = '本次读取未完成；未取得合同业务结果，可重试。';
  } else if (result?.kind === 'DEFINITION_READABLE') {
    title = '定义已读取 · AVAILABLE';
    text = '取得匹配的定义快照。定义读取不构成行动授权。';
  } else if (result?.kind === 'DEFINITION_UNKNOWN') {
    title = '定义未知 · UNKNOWN';
    text = '合法身份未找到；不代表空目录或读取错误。';
  } else if (result?.kind === 'DEFINITION_READ_UNAVAILABLE') {
    title = '读取不可用 · UNAVAILABLE';
    text = 'Reader 未能提供可信读取结果；不代表设备状态。';
  } else if (result?.kind === 'DEFINITION_READ_NOT_COMPLETED') {
    title = result.condition === FrontendCondition.LOADING ? '正在读取 · Loading' : '前端读取错误';
    text =
      result.condition === FrontendCondition.LOADING
        ? '等待本次请求；旧快照不作为当前结果。'
        : '本次未取得合同业务结果，可重试。';
  }
  return (
    <section
      className="ca-center__result"
      aria-label="本次查询结果"
      aria-live="polite"
      aria-atomic="true"
    >
      <h2 data-read-status>{title}</h2>
      <p>{text}</p>
      {state.submittedId !== null && (
        <p>
          已提交 ID：<code data-submitted-id>{state.submittedId}</code>
        </p>
      )}
    </section>
  );
}

export function CenterReadInformation({ state }: { readonly state: CenterReadState }) {
  return (
    <details className="ca-center__information" data-read-information>
      <summary>
        <Icon name="info" />
        数据来源与读取信息
      </summary>
      <div>
        <p>
          读取模式：<code>{CENTER_DATA_MODE}</code> · NON_AUTHORITY / 非行动授权。
        </p>
        <p>
          通过既有真实本地 HTTP
          链读取合成定义。定义合同不提供装备资产或动态状态；本地装备在独立只读工作区读取。
        </p>
        <p>新鲜度未知 / Freshness unknown：合同未提供观察时间、有效期、边界及验证信息。</p>
        <p>本次请求序号：{state.requestId}。集合仅保存在本次页面会话内存中，不是全量目录。</p>
        <p>
          详情合同字段：<code>{fields.map(([key]) => key).join(' · ')}</code>
        </p>
        <p>定义版本与聚合版本分别呈现；定义生命周期不代表设备运行状态。</p>
      </div>
    </details>
  );
}

export function CatalogWorkspace({
  state,
  load,
  cancel,
  showDetail,
}: {
  readonly state: CatalogReadState;
  readonly load: () => void;
  readonly cancel: () => void;
  readonly showDetail: (
    snapshot: CapabilityDefinitionSnapshotV1,
    trigger: HTMLButtonElement,
  ) => void;
}) {
  const [text, setText] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const items = state.result?.status === 'SUCCESS' ? state.result.items : [];
  const filtered = filterDefinitions(items, { text, type, status });
  const options = definitionFilterOptions(items);
  const title =
    state.phase === 'NOT_LOADED'
      ? '目录尚未加载'
      : state.phase === 'LOADING'
        ? '正在加载目录 · Loading'
        : state.phase === 'WIRE_INVALID'
          ? '目录 Wire Invalid'
          : state.phase === 'TRANSPORT_ERROR'
            ? '目录传输错误'
            : state.result?.status === 'SOURCE_UNAVAILABLE'
              ? '目录来源不可读 · SOURCE_UNAVAILABLE'
              : items.length === 0
                ? '目录读取成功 · 空目录'
                : '目录读取成功';
  return (
    <section className="ca-center__card ca-center__catalog" aria-labelledby="ca-catalog-title">
      <div className="ca-center__section-heading">
        <div>
          <p className="ca-center__eyebrow">DEFINITION CATALOG</p>
          <h2 id="ca-catalog-title">
            <Icon name="layers" />
            能力定义目录
          </h2>
        </div>
        <button type="button" className="ca-center__primary" onClick={load}>
          {state.phase === 'NOT_LOADED' ? '加载能力定义目录' : '重新加载能力定义目录'}
        </button>
      </div>
      <p className="ca-center__scope">本地合成 Owner 当前已发布定义 · 只读 · 不构成行动授权</p>
      <div className="ca-center__catalog-feedback" aria-live="polite" aria-atomic="true">
        <h3 data-catalog-status>{title}</h3>
        {state.phase === 'NOT_LOADED' && <p>主动加载后查看定义名称、类型与版本。</p>}
        {state.phase === 'LOADING' && (
          <>
            <p>等待本次 HTTP 读取；上次结果不作为本次目录。</p>
            <button type="button" onClick={cancel}>
              取消目录读取
            </button>
          </>
        )}
        {(state.phase === 'TRANSPORT_ERROR' || state.phase === 'WIRE_INVALID') && (
          <p>本次未取得可信目录。可重新加载；不会使用示例列表替代失败结果。</p>
        )}
        {state.result?.status === 'SOURCE_UNAVAILABLE' && (
          <p>Owner 未能提供可信目录；此结果不是成功空目录，可重新加载。</p>
        )}
      </div>
      {state.result?.status === 'SUCCESS' && (
        <>
          <div className="ca-center__catalog-meta">
            <span data-catalog-count>
              本次返回 {items.length} 条 · 当前筛选 {filtered.length} 条
            </span>
            <span>
              {state.result.coverage === 'TRUNCATED_AT_LIMIT'
                ? '本次读取到上限，结果已截断'
                : '本次来源范围内未截断'}
            </span>
          </div>
          <div className="ca-center__filters">
            <label>
              目录名称 / ID 筛选
              <input value={text} onChange={(event) => setText(event.target.value)} />
            </label>
            <label>
              目录能力类型
              <select
                aria-label="目录能力类型"
                value={type}
                onChange={(event) => setType(event.target.value)}
              >
                <option value="">全部返回类型</option>
                {options.types.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <label>
              目录定义生命周期
              <select
                aria-label="目录定义生命周期"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">全部返回生命周期</option>
                {options.statuses.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="ca-center__scope">仅筛选本次后端返回结果；不发起服务器搜索。</p>
          {items.length === 0 ? (
            <p className="ca-center__empty">当前来源没有已发布定义；目录 HTTP 读取成功。</p>
          ) : filtered.length === 0 ? (
            <p className="ca-center__empty">目录当前筛选无匹配。</p>
          ) : (
            <ul className="ca-center__catalog-entries">
              {filtered.map((snapshot) => (
                <li key={snapshot.capabilityId} data-catalog-id={snapshot.capabilityId}>
                  <div className="ca-center__catalog-name">
                    <h3>{snapshot.displayName}</h3>
                    <code>{snapshot.capabilityId}</code>
                  </div>
                  <span className="ca-center__catalog-type">{snapshot.capabilityType}</span>
                  <span className="ca-center__published">{snapshot.definitionStatus}</span>
                  <span className="ca-center__catalog-version">
                    定义版本 {snapshot.definitionVersion}
                  </span>
                  <button
                    type="button"
                    className="ca-center__secondary"
                    aria-label={`查看目录定义详情：${snapshot.capabilityId}`}
                    onClick={(event) => showDetail(snapshot, event.currentTarget)}
                  >
                    查看详情
                  </button>
                </li>
              ))}
            </ul>
          )}
          <details className="ca-center__catalog-wire">
            <summary>来源范围与读取限制</summary>
            <p>
              {state.result.scope} · limit {state.result.limit} · {state.result.coverage}
              。仅当前可读取定义；无历史版本、全机构数量或并发原子快照承诺。PUBLISHED
              仅是定义生命周期。
            </p>
          </details>
        </>
      )}
    </section>
  );
}
/** This exact development read surface has no production consumer entry. */
export function CapabilityEquipmentCenter({
  port,
  catalogPort,
}: {
  readonly port?: CapabilityDefinitionReadPort;
  readonly catalogPort?: CapabilityDefinitionCatalogReadPort;
}) {
  const origin = apiBaseOrigin();
  const enabled =
    typeof window !== 'undefined' &&
    (configuredApiOrigin() !== null || (import.meta.env.DEV && port !== undefined));
  const livePort = useMemo(() => {
    if (!enabled) return null;
    if (port) return port;
    try {
      return createLiveCapabilityDefinitionAdapter({ baseOrigin: origin });
    } catch {
      return null;
    }
  }, [enabled, port, origin]);
  const liveCatalog = useMemo(() => {
    if (!enabled) return null;
    if (catalogPort) return catalogPort;
    try {
      return createLiveCapabilityDefinitionCatalogAdapter({ baseOrigin: origin });
    } catch {
      return null;
    }
  }, [enabled, catalogPort, origin]);
  /**
   * The SYN knowledge plane's port, under the SAME enablement rule this page already uses for its
   * definition and catalog reads: a configured build origin, or an explicit development port. It
   * is a different read plane, not a different authority — the requirements it shows are the
   * experimental projection, and the exact definition pins it resolves come from this same 05
   * Owner.
   */
  const knowledgePort = useMemo(() => {
    if (!enabled) return null;
    try {
      return createLiveScenarioKnowledgeAdapter({ baseOrigin: origin });
    } catch {
      return null;
    }
  }, [enabled, origin]);
  const [catalog, setCatalog] = useState(initialCatalogReadState);
  const catalogSession = useRef<ReturnType<typeof createCatalogReadSession> | null>(null);
  const detailScroll = useRef(0);
  const [detailSource, setDetailSource] = useState('本次会话已读取快照');
  useEffect(() => {
    if (!liveCatalog) return;
    const reader = createCatalogReadSession(liveCatalog, setCatalog);
    catalogSession.current = reader;
    return () => {
      reader.dispose();
      catalogSession.current = null;
    };
  }, [liveCatalog]);
  const liveRequested = typeof window !== 'undefined' && synLiveAssetEnabled();
  const managementAvailable = syntheticBusinessEnabled() || liveRequested;
  const [workspace, setWorkspace] = useState<'definitions' | 'assets' | 'management'>(() => {
    const selected =
      typeof window === 'undefined'
        ? null
        : new URLSearchParams(window.location.search).get('workspace');
    if (selected === 'definitions' || selected === 'assets') return selected;
    return liveRequested ? 'management' : 'assets';
  });
  function selectWorkspace(next: 'definitions' | 'assets' | 'management') {
    const url = new URL(window.location.href);
    url.searchParams.set('workspace', next);
    if (next === 'management') url.searchParams.set('synLive', '1');
    window.history.replaceState(window.history.state, '', url);
    setWorkspace(next);
  }
  const [input, setInput] = useState('');
  const [state, setState] = useState(initialCenterReadState);
  const [text, setText] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState<CapabilityDefinitionSnapshotV1 | null>(null);
  const session = useRef<ReturnType<typeof createCenterReadSession> | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const detailTrigger = useRef<HTMLButtonElement | null>(null);
  const inputElement = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!enabled || !livePort) return;
    const reader = createCenterReadSession(livePort, setState);
    session.current = reader;
    return () => {
      reader.dispose();
      session.current = null;
    };
  }, [enabled, livePort]);
  useEffect(() => {
    if (detail && dialog.current && !dialog.current.open) {
      dialog.current.showModal();
      closeButton.current?.focus();
    }
  }, [detail]);
  function closeDetail() {
    const wasOpen = dialog.current?.open === true;
    dialog.current?.close();
    setDetail(null);
    if (!wasOpen) return;
    if (detailTrigger.current?.isConnected) detailTrigger.current.focus({ preventScroll: true });
    else inputElement.current?.focus({ preventScroll: true });
    window.scrollTo(0, detailScroll.current);
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    closeDetail();
    if (enabled) void session.current?.submit(input);
  }
  const filtered = filterDefinitions(state.collection, { text, type, status });
  const options = definitionFilterOptions(state.collection);
  const loading =
    state.result?.kind === 'DEFINITION_READ_NOT_COMPLETED' &&
    state.result.condition === FrontendCondition.LOADING;
  return (
    <>
      <PlatformHeader activePage="capability-equipment" />
      <main id="ca-main" className="p01 ca-center">
        <BusinessWorkspaceSelector />
        <div className="ca-center__notice" data-read-notice>
          <Icon name="info" />
          {workspace === 'management'
            ? '本地合成测试环境 · 保存与提交以后端回执为准 · 不构成行动授权'
            : '本地合成数据 · 只读 · 不构成行动授权'}
        </div>
        <header className="p01-titlebar ca-center__header">
          <TitleBackdrop />
          <div className="ca-center__heading">
            <p className="ca-center__eyebrow">CAPABILITY & EQUIPMENT</p>
            <h1>能力与装备中心</h1>
            <p>浏览本地装备与提供者，核对能力定义及数据可信范围</p>
          </div>
          <a href="/demo/page-01">返回总览</a>
        </header>
        <div className="ca-center__content">
          {!enabled ? (
            <section className="ca-center__card" aria-label="未开放状态">
              <h2>生产态读取入口未开放</h2>
              <p>本切片仅在开发态连接既有本地合成服务。生产预览不发起能力定义请求。</p>
            </section>
          ) : (
            <>
              <div
                className="ca-center__workspace-tabs"
                role="tablist"
                aria-label="能力与装备工作区"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={workspace === 'definitions'}
                  onClick={() => {
                    closeDetail();
                    selectWorkspace('definitions');
                  }}
                >
                  能力定义
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={workspace === 'assets'}
                  onClick={() => {
                    closeDetail();
                    selectWorkspace('assets');
                  }}
                >
                  本地装备／提供者
                </button>
                {managementAvailable && (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={workspace === 'management'}
                    onClick={() => {
                      closeDetail();
                      selectWorkspace('management');
                    }}
                  >
                    装备管理
                  </button>
                )}
              </div>
              {managementAvailable && (
                <div hidden={workspace !== 'management'}>
                  <SynLiveLocalAsset />
                </div>
              )}
              {!managementAvailable && (
                <details>
                  <summary>装备管理交互演示（不保存到数据库）</summary>
                  <AssetDataManagementPrototype />
                </details>
              )}
              {workspace === 'assets' && (
                <LocalAssetPanel
                  onDefinition={(id) => {
                    closeDetail();
                    selectWorkspace('definitions');
                    setInput(id);
                    void session.current?.submit(id);
                    requestAnimationFrame(() => inputElement.current?.focus());
                  }}
                />
              )}
              <div className="ca-center__definition-workspace" hidden={workspace !== 'definitions'}>
                <CatalogWorkspace
                  state={catalog}
                  load={() => {
                    closeDetail();
                    void catalogSession.current?.load();
                  }}
                  cancel={() => catalogSession.current?.cancel()}
                  showDetail={(snapshot, trigger) => {
                    detailScroll.current = window.scrollY;
                    detailTrigger.current = trigger;
                    setDetailSource('本次后端目录返回快照');
                    setDetail(snapshot);
                  }}
                />
                <section
                  className="ca-center__card ca-center__query-card"
                  aria-labelledby="ca-query-title"
                >
                  <h2 id="ca-query-title">
                    <Icon name="search" />按 ID 读取定义
                  </h2>
                  <form className="ca-center__query" onSubmit={submit}>
                    <label htmlFor="ca-id">能力 ID</label>
                    <input
                      id="ca-id"
                      ref={inputElement}
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      autoComplete="off"
                    />
                    <button type="submit">查询定义</button>
                    {loading && (
                      <button
                        type="button"
                        className="ca-center__secondary"
                        onClick={() => session.current?.cancel()}
                      >
                        取消本次读取
                      </button>
                    )}
                  </form>
                  <div className="ca-center__query-hint">
                    <p>仅明确提交才读取；同 ID 成功重读会更新集合。</p>
                    <button
                      type="button"
                      className="ca-center__text-button"
                      onClick={() => {
                        setInput(' synthetic-browser-中文 +/%?# ');
                        inputElement.current?.focus();
                      }}
                    >
                      填入合成示例 ID
                    </button>
                  </div>
                  <CenterReadSummary state={state} />
                  {state.submittedId !== null && !loading && (
                    <button
                      type="button"
                      className="ca-center__secondary"
                      onClick={() => {
                        closeDetail();
                        void session.current?.submit(state.submittedId ?? '');
                      }}
                    >
                      重新读取已提交 ID
                    </button>
                  )}
                </section>
                <section className="ca-center__card" aria-labelledby="ca-collection-title">
                  <div className="ca-center__section-heading">
                    <h2 id="ca-collection-title">
                      <Icon name="layers" />
                      本次已读取集合
                    </h2>
                    <span data-collection-count>
                      本次集合 {state.collection.length} 条 · 当前筛选 {filtered.length} 条
                    </span>
                  </div>
                  <p className="ca-center__scope">
                    本次页面会话的成功读取集合 · 非全量目录。未知、不可用及失败读取不计入。
                  </p>
                  <div className="ca-center__filters">
                    <label>
                      名称 / ID 筛选
                      <input value={text} onChange={(event) => setText(event.target.value)} />
                    </label>
                    <label>
                      能力类型
                      <select
                        aria-label="能力类型"
                        value={type}
                        onChange={(event) => setType(event.target.value)}
                      >
                        <option value="">全部已读取类型</option>
                        {options.types.map((value) => (
                          <option key={value}>{value}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      定义生命周期
                      <select
                        aria-label="定义生命周期"
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                      >
                        <option value="">全部已读取生命周期</option>
                        {options.statuses.map((value) => (
                          <option key={value}>{value}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p data-filter-summary>
                    当前筛选：{text || '全部名称 / ID'} · {type || '全部已读取类型'} ·{' '}
                    {status || '全部已读取生命周期'}
                  </p>
                  {state.collection.length === 0 ? (
                    <p className="ca-center__empty">本次尚无成功读取的定义；这不是全量目录统计。</p>
                  ) : filtered.length === 0 ? (
                    <p className="ca-center__empty">当前筛选无匹配；仅筛选本次已读取集合。</p>
                  ) : (
                    <ul className="ca-center__entries">
                      {filtered.map((snapshot) => (
                        <li key={snapshot.capabilityId}>
                          <div>
                            <h3>{snapshot.displayName}</h3>
                            <code>{snapshot.capabilityId}</code>
                            <p>
                              {snapshot.capabilityType} · {snapshot.definitionStatus}
                              （定义生命周期）
                            </p>
                            <p>定义版本 {snapshot.definitionVersion}</p>
                          </div>
                          <button
                            type="button"
                            className="ca-center__secondary"
                            aria-label={`查看定义详情：${snapshot.capabilityId}`}
                            onClick={(event) => {
                              detailTrigger.current = event.currentTarget;
                              detailScroll.current = window.scrollY;
                              setDetailSource('本次会话已读取快照');
                              setDetail(snapshot);
                            }}
                          >
                            查看详情
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                <dialog
                  ref={dialog}
                  className="ca-center__dialog"
                  aria-labelledby="ca-detail-title"
                  onCancel={(event) => {
                    event.preventDefault();
                    closeDetail();
                  }}
                >
                  {detail && (
                    <>
                      <div className="ca-center__section-heading">
                        <h2 id="ca-detail-title">定义详情</h2>
                        <button ref={closeButton} type="button" onClick={closeDetail}>
                          关闭详情并返回
                        </button>
                      </div>
                      <p>{detailSource}</p>
                      <DefinitionFields snapshot={detail} />
                      <button
                        type="button"
                        className="ca-center__secondary"
                        onClick={() => {
                          const id = detail.capabilityId;
                          closeDetail();
                          setInput(id);
                          void session.current?.submit(id);
                        }}
                      >
                        重新按 ID 读取
                      </button>
                      <p>
                        来源：REAL_LOCAL_SYNTHETIC_HTTP · NON_AUTHORITY ·
                        新鲜度未知。合同未提供边界、关联装备、验证、观察时间及有效期。
                      </p>
                    </>
                  )}
                </dialog>
              </div>
            </>
          )}
          {workspace !== 'management' && <CenterReadInformation state={state} />}
          {workspace === 'definitions' && <ScenarioKnowledgeChain port={knowledgePort} compact />}
        </div>
      </main>
    </>
  );
}
